import { LightboxImageOpenedEvent } from '@/custom-events';
import { pnumber } from '@cp949/pjs';
import { useEffect, useMemo, useState } from 'react';
import Lightbox from 'react-image-lightbox';
import 'react-image-lightbox/style.css';

export type ImageViewLightboxProps = {
  open: boolean;
  imageUrls: string[];
  startIndex: number;
  disableEscKeydown?: boolean;
  onClose: () => void;
};

type ImageItem = {
  url: string;
  prev?: string;
  next?: string;
};

/**
 * 이미지 URL목록을 링크드리스트로 만든다
 */
const makeLinkedList = (urls: string[]): ImageItem[] => {
  const list: ImageItem[] = [];
  const total = urls.length;
  for (let i = 0; i < total; i++) {
    list.push({
      url: urls[i],
      prev: i - 1 >= 0 ? urls[i - 1] : undefined,
      next: i + 1 < total ? urls[i + 1] : undefined,
    });
  }
  return list;
};

export default function ImageViewLightbox(props: ImageViewLightboxProps) {
  const { open, onClose, disableEscKeydown = false, imageUrls = [], startIndex = 0 } = props;
  const [slidingIndex, setSlidingIndex] = useState(() => startIndex);
  const imageItems = useMemo(() => makeLinkedList(imageUrls), [imageUrls]);

  // 🔹 Lightbox 오픈 이벤트
  useEffect(() => {
    if (!open) return;
    LightboxImageOpenedEvent.send({ opened: true });
    return () => {
      LightboxImageOpenedEvent.send({ opened: false });
    };
  }, [open]);

  // 🔹 이미지가 없으면 바로 닫기
  useEffect(() => {
    if (open && (!imageUrls || imageUrls.length === 0)) {
      onClose();
    }
  }, [open, imageUrls, onClose]);

  // 🔹 키보드 이벤트 처리: ESC, 좌우 화살표
  useEffect(() => {
    if (!open) return;

    const onKeyDown = (ev: KeyboardEvent) => {
      if (!disableEscKeydown && ev.key === 'Escape') {
        ev.preventDefault();
        onClose();
      } else if (ev.key === 'ArrowRight') {
        ev.preventDefault();
        setSlidingIndex((prev) => pnumber.clamp(prev + 1, 0, imageItems.length - 1));
      } else if (ev.key === 'ArrowLeft') {
        ev.preventDefault();
        setSlidingIndex((prev) => pnumber.clamp(prev - 1, 0, imageItems.length - 1));
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, imageItems.length, disableEscKeydown, onClose]);

  if (!open || imageItems.length === 0) {
    return null;
  }

  const currentItem: ImageItem = imageItems[slidingIndex];
  const pageNumText =
    imageItems.length > 1 ? `${slidingIndex + 1} / ${imageItems.length}` : undefined;

  return (
    <Lightbox
      mainSrc={currentItem.url}
      prevSrc={currentItem.prev}
      nextSrc={currentItem.next}
      onMoveNextRequest={() =>
        setSlidingIndex(pnumber.clamp(slidingIndex + 1, 0, imageItems.length - 1))
      }
      onMovePrevRequest={() =>
        setSlidingIndex(pnumber.clamp(slidingIndex - 1, 0, imageItems.length - 1))
      }
      imageCaption={pageNumText}
      onCloseRequest={onClose}
      reactModalStyle={{ overlay: { zIndex: 1500 } }}
    />
  );
}
